import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, Mail, Phone, Globe, Calendar, MapPin,
  DollarSign, ClipboardList, CheckCircle, Star, Edit3, Save, X, Loader2
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';
import { useTranslation, LANGUAGE_OPTIONS } from '../translations';

export default function CustomerProfile() {
  const navigate = useNavigate();
  const { user, token, language, loadFromStorage, setLanguage } = useAuthStore();
  const { t } = useTranslation(language);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', language: 'de' });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadFromStorage(); }, [loadFromStorage]);
  useEffect(() => {
    if (token) loadProfile();
    else setLoading(false);
  }, [token]);

  const loadProfile = async () => {
    try {
      const res = await api.get('/customer/profile');
      setProfile(res.data);
      setForm({
        name: res.data.user.name || '',
        phone: res.data.user.phone || '',
        language: res.data.user.language || 'de',
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/customer/profile', null, { params: form });
      setLanguage(form.language);
      setEditing(false);
      loadProfile();
    } catch (err) {
      alert('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-orange-500" />
      </div>
    );
  }

  if (!user || !token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center p-8">
          <User size={48} className="mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Anmeldung erforderlich</h2>
          <a href="/" className="text-orange-500 hover:underline">Zur Startseite</a>
        </div>
      </div>
    );
  }

  const filteredBookings = (profile?.bookings || []).filter(b => {
    if (filter === 'all') return true;
    return b.status === filter;
  });

  const STATUS = {
    open: { label: 'Offen', cls: 'bg-blue-100 text-blue-700' },
    bidding: { label: 'Angebote', cls: 'bg-yellow-100 text-yellow-700' },
    booked: { label: 'Gebucht', cls: 'bg-emerald-100 text-emerald-700' },
    completed: { label: 'Abgeschlossen', cls: 'bg-slate-100 text-slate-600' },
    cancelled: { label: 'Storniert', cls: 'bg-red-100 text-red-700' },
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="customer-profile">
      {/* Nav */}
      <nav className="bg-white border-b border-slate-100 px-6 py-3 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-slate-100 rounded-lg">
              <ArrowLeft size={20} className="text-slate-600" />
            </button>
            <a href="/" className="flex items-baseline gap-0.5">
              <span className="text-xl font-extrabold text-orange-500">366</span>
              <span className="text-xs font-semibold text-slate-800">Transfer</span>
            </a>
          </div>
          <span className="text-sm text-slate-500">Mein Profil</span>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 text-2xl font-bold">
                {profile?.user?.name?.charAt(0)?.toUpperCase() || 'K'}
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800" data-testid="profile-name">{profile?.user?.name}</h1>
                <p className="text-sm text-slate-500 flex items-center gap-1"><Mail size={14} /> {profile?.user?.email}</p>
              </div>
            </div>
            {!editing ? (
              <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-600" data-testid="edit-profile-btn">
                <Edit3 size={14} /> Bearbeiten
              </button>
            ) : (
              <div className="flex gap-2">
                <button onClick={() => setEditing(false)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X size={18} className="text-slate-400" /></button>
                <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 disabled:opacity-50" data-testid="save-profile-btn">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern
                </button>
              </div>
            )}
          </div>

          {editing ? (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="edit-form">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Name</label>
                <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-orange-500 outline-none" data-testid="edit-name" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Telefon</label>
                <input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-orange-500 outline-none" data-testid="edit-phone" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Sprache</label>
                <select value={form.language} onChange={e => setForm(f => ({...f, language: e.target.value}))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-orange-500 outline-none" data-testid="edit-language">
                  {LANGUAGE_OPTIONS.map(l => <option key={l.code} value={l.code}>{l.flag} {l.name}</option>)}
                </select>
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2 text-slate-600"><Phone size={14} className="text-slate-400" /> {profile?.user?.phone || '—'}</div>
              <div className="flex items-center gap-2 text-slate-600"><Globe size={14} className="text-slate-400" /> {LANGUAGE_OPTIONS.find(l => l.code === profile?.user?.language)?.name || profile?.user?.language}</div>
              <div className="flex items-center gap-2 text-slate-600"><Calendar size={14} className="text-slate-400" /> Mitglied seit {profile?.user?.created_at?.slice(0, 10)}</div>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { l: 'Buchungen', v: profile?.stats?.total_bookings || 0, icon: ClipboardList, c: 'text-blue-600 bg-blue-50' },
            { l: 'Abgeschlossen', v: profile?.stats?.completed || 0, icon: CheckCircle, c: 'text-emerald-600 bg-emerald-50' },
            { l: 'Anfragen', v: profile?.stats?.total_requests || 0, icon: MapPin, c: 'text-purple-600 bg-purple-50' },
            { l: 'Ausgaben', v: `€${profile?.stats?.total_spent || 0}`, icon: DollarSign, c: 'text-amber-600 bg-amber-50' },
          ].map(s => {
            const I = s.icon;
            return (
              <div key={s.l} className="bg-white rounded-xl p-4 border border-slate-100">
                <div className={`w-8 h-8 rounded-lg ${s.c} flex items-center justify-center mb-2`}><I size={16} /></div>
                <p className="text-xl font-bold text-slate-800">{s.v}</p>
                <p className="text-xs text-slate-400">{s.l}</p>
              </div>
            );
          })}
        </div>

        {/* Booking History */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-bold text-slate-800">Buchungshistorie</h2>
            <div className="flex gap-1.5">
              {['all', 'booked', 'completed', 'open'].map(f => (
                <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1 rounded-lg text-xs font-medium ${filter === f ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`filter-${f}`}>
                  {f === 'all' ? 'Alle' : STATUS[f]?.label || f}
                </button>
              ))}
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {filteredBookings.length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                <ClipboardList size={32} className="mx-auto mb-2 opacity-50" />
                <p>Keine Buchungen gefunden</p>
              </div>
            ) : (
              filteredBookings.map(booking => (
                <div key={booking.id} className="px-6 py-4 hover:bg-slate-50 transition-colors" data-testid={`booking-${booking.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-start gap-2 mb-2">
                        <div className="flex flex-col items-center mt-1">
                          <div className="w-2 h-2 rounded-full bg-orange-500" />
                          <div className="w-0.5 h-4 bg-slate-200" />
                          <div className="w-2 h-2 rounded-full bg-emerald-500" />
                        </div>
                        <div>
                          <p className="text-sm text-slate-800">{booking.pickup_location?.address?.split(',').slice(0, 2).join(',') || '—'}</p>
                          <p className="text-sm text-slate-800 mt-1">{booking.dropoff_location?.address?.split(',').slice(0, 2).join(',') || '—'}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-slate-500 ml-4">
                        <span className="flex items-center gap-1"><Calendar size={11} /> {booking.pickup_date?.slice(0, 10)}</span>
                        <span>{booking.distance_km} km</span>
                        <span>{booking.adults + (booking.children || 0)} Pers.</span>
                        {booking.booked_offer && (
                          <span className="font-medium text-emerald-600">&euro;{booking.booked_offer.price} | {booking.booked_offer.driver_name}</span>
                        )}
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${STATUS[booking.status]?.cls || 'bg-slate-100 text-slate-600'}`}>
                      {STATUS[booking.status]?.label || booking.status}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
